import { LoaderCircle } from 'lucide-react';
import * as React from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { RudderstackBrandLogo } from '@/_shared/components/RudderstackBrandLogo';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { buttonVariants } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';

/**
 * The gateway-card family — the shared building blocks for every standalone
 * centered-card screen shown *outside* the app shell (login, accept-invite,
 * workspace creation/selection, session errors). Screens compose these zones
 * top-to-bottom: GatewayBrand → GatewayContent → GatewayActions (+ GatewayRow lists).
 *
 * Everything for the gateway family lives in this one file, including the loading
 * spinner and the "Powered by" lockup. There is deliberately no `GatewayCard`
 * (GatewayShell IS the card) and no primary-button wrapper — gateway CTAs are just
 * `<Button variant="primary" size="lg" className="w-full">`.
 */

/**
 * "Powered by RudderStack" lockup — the RudderStack corporate wordmark rendered inline.
 * Uses fill="currentColor" tinted by the `--brand-mark` token (brand blue in light, white in
 * dark), matching RudderstackBrandLogo — so it stays legible on the dark surface.
 */
export function PoweredByRudderstack({ className }: { className?: string }): React.ReactElement {
  return (
    <p className={cn('flex items-center justify-center gap-1.5 text-sm text-muted-foreground', className)}>
      Powered by
      <svg
        role="img"
        aria-label="RudderStack"
        viewBox="55 8 200 28"
        fill="none"
        className="h-3.5 w-auto text-brand-mark"
      >
        <title>RudderStack</title>
        <path
          d="M65.8286 14.522H61.2815v6.261h4.6171C68.6968 20.783 69.8861 19.7687 69.8861 17.705c0-2.1337-1.01439999999999-3.183-4.0575-3.183zM70.5507 30.332 65.269 23.1615H61.2815V30.332h-3.148V11.9336H66.1085C70.7605 11.9336 72.9991 13.9973 72.9991 17.635c0 2.8682-1.574 4.6871-4.5121 5.2817l5.5964 7.0656V30.332H70.5507zm14.1625-2.3435c3.7077.0 4.827-1.9238 4.827-4.827V11.9336h3.113V23.3364c0 4.792-2.7633 7.3454-7.94 7.3454s-8.1849-2.5184-8.1849-7.3454V11.9336h3.1481v11.088c0 2.9382 1.1542 4.9669 5.0368 4.9669zM103.53 14.522H99.4376V27.7437H103.74c4.86200000000001.0 6.786-2.4835 6.786-6.7508C110.526 16.4807 108.427 14.522 103.53 14.522zm-7.2405 15.81V11.9336h7.6255C110.526 11.9336 113.639 15.1516 113.639 20.9929 113.639 26.7993 109.966 30.332 103.95 30.332H96.2895zm27.0695-15.81h-4.092V27.7437H123.569C128.431 27.7437 130.355 25.2602 130.355 20.9929 130.355 16.4807 128.256 14.522 123.359 14.522zm-7.24 15.81V11.9336h7.625C130.355 11.9336 133.468 15.1516 133.468 20.9929 133.468 26.7993 129.795 30.332 123.779 30.332h-7.66zm19.83.0V11.9336h13.606v2.6583H139.097v5.2118H148.506v2.3435H139.097v5.4915H149.52V30.332H135.949zm24.382-15.81h-4.547v6.261H160.401C163.199 20.783 164.388 19.7687 164.388 17.705 164.388 15.5713 163.374 14.522 160.331 14.522zM165.053 30.332 159.771 23.1615H155.784V30.332h-3.148V11.9336H160.61C165.263 11.9336 167.501 13.9973 167.501 17.635c0 2.8682-1.57400000000001 4.6871-4.512 5.2817l5.596 7.0656V30.332H165.053zM178.271 30.7168C172.639 30.6818 170.331 28.1634 169.841 24.6306L172.814 24.001C173.339 26.7643 175.088 28.1984 178.376 28.1984 181.384 28.1984 182.678 27.149 182.678 25.5051c0-1.714-1.22399999999999-2.5534-5.072-3.183C172.29 21.4476 170.611 19.5238 170.611 16.7256 170.611 13.5426 173.024 11.5489 177.816 11.5489c4.827.0 6.786 2.1686 7.48499999999999 5.4565L182.223 17.6C181.664 15.2565 180.614 14.0323 177.676 14.0323 174.983 14.0323 173.689 14.9767 173.689 16.5857c0 1.4341.873999999999995 2.4484 5.072 3.183 5.666.979400000000002 7.03 2.9381 7.03 5.5265C185.791 28.5831 183.587 30.7168 178.271 30.7168zm8.179-15.9849V11.9336h15.495v2.7983h-6.191V30.332h-3.113V14.7319H186.45zM215.037 30.332 213.149 25.3652H204.404L202.515 30.332H199.297L206.748 11.9336h4.197L218.36 30.332H215.037zM206.188 20.7481l-.875 2.2735H212.274L211.4 20.7481C210.63 18.7893 209.861 16.5157 209.266 14.5919H208.392C207.727 16.5157 206.922 18.7543 206.188 20.7481zm21.754 9.9687C222.031 30.7168 218.708 27.0091 218.708 21.0978c0-5.9112 3.53300000000002-9.5489 9.339-9.5489C232.979 11.5489 235.987 14.1372 236.687 18.5095L233.714 19.1041C233.049 15.9561 231.3 14.2422 227.977 14.2422 223.78 14.2422 221.821 17.0754 221.821 21.0978c0 4.2674 1.959 6.9257 6.12100000000001 6.9257C231.475 28.0235 233.154 26.2746 233.819 23.0566L236.792 23.5813C236.092 28.0235 233.084 30.7168 227.942 30.7168zM251.872 30.332l-7.661-7.4503H242.498V30.332h-3.149V11.9336h3.149v8.4297H244.037L251.592 11.9336H255.09v.3148L246.38 21.4126 255.509 29.9823V30.332H251.872z"
          fill="currentColor"
        />
      </svg>
    </p>
  );
}

/**
 * Outer scaffold + centered card. The card is flat (border only, no shadow) to match the
 * flattened `Card` primitive.
 *
 * Defaults to `max-w-sm text-center`. List/form screens pass `max-w-md` and drop centering
 * with `text-left` (the caller className is last in `cn()`, so it wins); a scrollable list card
 * additionally passes `flex max-h-[...] flex-col`.
 */
export function GatewayShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div
        className={cn(
          'w-full max-w-sm animate-fade-in rounded-2xl border border-border bg-card p-8 text-center',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** Zone 1 — the canonical brand lockup, centered, with an optional "Powered by" line below. */
export function GatewayBrand({ poweredBy = false }: { poweredBy?: boolean }): React.ReactElement {
  return (
    <div className="mb-8 flex flex-col items-center gap-4">
      <RudderstackBrandLogo />
      {poweredBy && <PoweredByRudderstack />}
    </div>
  );
}

export type GatewayVariant = 'success' | 'error' | 'warning' | 'info' | 'brand';

// Status-tile tints. success/error/warning/info are Callout's variant map (callout.tsx:10-16);
// `brand` is the blue/brand glyph treatment (bg-highlight-bg + text-primary — blue-600 is not a token).
const TILE_VARIANT_CLASSES: Record<GatewayVariant, string> = {
  success: 'bg-success-bg text-success',
  error: 'bg-danger-bg text-danger',
  warning: 'bg-warning-bg text-warning',
  info: 'bg-info-bg text-info',
  brand: 'bg-highlight-bg text-primary',
};

/**
 * Zone 2 — the content block. Renders one of two shapes:
 *  - Shape A `[icon, title, description]`: a status tile + h1 + description.
 *  - Shape B `[title, description, children]`: h1 + optional description + a content block.
 * Shape-C screens don't render GatewayContent at all. An optional small-caps `eyebrow`
 * sits above the icon/title. `variant` only tints the tile (meaningful when `icon` is set).
 */
export function GatewayContent({
  eyebrow,
  icon,
  variant = 'info',
  title,
  description,
  children,
}: {
  eyebrow?: string;
  icon?: React.ReactNode;
  variant?: GatewayVariant;
  /** ReactNode, not string, so a title can carry an inline token (see AcceptInvite). */
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div>
      {eyebrow && <p className="mb-3 text-xs font-bold tracking-wider text-muted-foreground uppercase">{eyebrow}</p>}
      {icon && (
        <div
          className={cn(
            "mx-auto mb-4 flex size-12 items-center justify-center rounded-xl [&_svg:not([class*='size-'])]:size-6",
            TILE_VARIANT_CLASSES[variant],
          )}
        >
          {icon}
        </div>
      )}
      <h1 className="text-xl font-semibold tracking-[-0.02em] text-foreground">{title}</h1>
      {description && <div className="mt-2 text-sm text-muted-foreground">{description}</div>}
      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}

/** Zone 3 — a primary action plus optional sub-actions, stacked. */
export function GatewayActions({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="mt-6 flex flex-col gap-3">{children}</div>;
}

/**
 * A full-width, left-aligned action-list row (workspace / persona / provider picker).
 * Composed from the `secondary` Button family so its hover/focus/disabled states match
 * the primitives. Disabled + `disabledReason` gets an ActionTooltip (a disabled button
 * fires no pointer events, so a bare Tooltip would never open).
 */
export function GatewayRow({
  title,
  subtitle,
  leading,
  trailing,
  onClick,
  disabled = false,
  disabledReason,
  loading = false,
}: {
  title: string;
  subtitle?: React.ReactNode;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  loading?: boolean;
}): React.ReactElement {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        buttonVariants({ variant: 'secondary' }),
        'h-auto w-full justify-start gap-3 px-4 py-3 text-left whitespace-normal',
      )}
    >
      {loading ? <LoaderCircle aria-hidden className="size-4 shrink-0 animate-spin text-muted-foreground" /> : leading}
      <span className="flex min-w-0 flex-1 flex-col">
        <TruncateWithTooltip className="font-medium">{title}</TruncateWithTooltip>
        {subtitle && <span className="text-sm text-muted-foreground">{subtitle}</span>}
      </span>
      {trailing && <span className="shrink-0">{trailing}</span>}
    </button>
  );

  if (disabled && disabledReason) {
    // ActionTooltip wraps its child in a focusable inline-flex span; stretch that span
    // to the row's full width so a disabled row lines up with its enabled siblings.
    return (
      <div className="[&>span]:flex [&>span]:w-full">
        <ActionTooltip show message={disabledReason}>
          {button}
        </ActionTooltip>
      </div>
    );
  }
  return button;
}

/**
 * The shared gateway loading spinner. `fullScreen` centers it in the viewport scaffold
 * (pre-shell route loaders); `inline` centers it within a gateway card. Both announce via
 * `role="status"` with an `aria-label` + `sr-only` label.
 */
export function GatewaySpinner({
  mode = 'inline',
  label = 'Loading',
}: {
  mode?: 'fullScreen' | 'inline';
  label?: string;
}): React.ReactElement {
  return (
    <div
      role="status"
      aria-label={label}
      className={cn('flex items-center justify-center', mode === 'fullScreen' ? 'min-h-screen bg-background' : 'py-10')}
    >
      <LoaderCircle aria-hidden className="size-6 animate-spin text-muted-foreground" />
      <span className="sr-only">{label}</span>
    </div>
  );
}
